// Agents who failed a criterion N times in a row on recent calls.
// Clicking the criterion name opens the criterion drawer narrowed to that agent.
import React, { useContext, useMemo } from 'react'
import { isEmpty } from 'lodash'
import moment from 'moment'

import { Card } from '@/components/cards/Card'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { apiService } from '@/api'

import { EntityClickContext } from '../helpers/dashboardContexts'
import { WidgetActionHint } from '../components/WidgetActionHint'
import { ShowCallsButton } from '../components/ShowCallsButton'
import {
  buildDashboardParams,
  useDashboardOrgId,
  useFiltersForPickedScope,
} from '../helpers/dashboardParams'
import { useDashboardFetch } from '../hooks/useDashboardFetch'

// Default streak threshold the BE will use when we don't pass one.
// Bumping this on the FE keeps the widget self-contained — the only
// reason to call it out is so the empty-state copy can name the
// threshold.
const MIN_STREAK = 3

const formatDate = (iso) => {
  if (!iso) {
    return '—'
  }
  // UTC from the service; new Date() would read an unmarked string as local.
  const parsed = moment.utc(iso).toDate()
  if (Number.isNaN(parsed.getTime())) {
    return '—'
  }
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export const FailureStreakAlertsWidget = ({ scope }) => {
  const filters = useFiltersForPickedScope(scope)
  const orgId = useDashboardOrgId()
  const clickEntity = useContext(EntityClickContext)
  const url = useMemo(() => {
    if (!orgId) {
      return null
    }
    const params = buildDashboardParams(filters, orgId)
    params.set('min_streak', String(MIN_STREAK))
    return `${apiService.scorecard}/scoring/scores/clickhouse/criterion_failure_streaks?${params.toString()}`
  }, [orgId, filters])
  const { data, loading, error: err } = useDashboardFetch(url)
  const items = data?.items

  // Pre-sort: longest streak first; ties broken by most-recent fail
  // (the BE already orders this way, but we re-sort defensively so
  // partial fetches still look sane). AdvancedTable's default sort is
  // streak-desc and its sort is stable, so this tie order survives.
  const rows = useMemo(() => {
    const sorted = (items || []).slice().sort((rowA, rowB) => {
      if (rowB.streak_length !== rowA.streak_length) {
        return rowB.streak_length - rowA.streak_length
      }
      const timeA = new Date(rowA.last_failed_at).getTime() || 0
      const timeB = new Date(rowB.last_failed_at).getTime() || 0
      return timeB - timeA
    })
    return sorted.map((row) => {
      const agentName =
        `${row.first_name || ''} ${row.last_name || ''}`.trim() || `User ${row.user_id}`
      // Stable composite key from the (agent, criterion, scorecard) tuple the
      // streak alert represents — no array index, so it survives re-sorts/refetches.
      return {
        ...row,
        id: `${row.user_id}-${row.criteria_id}-${row.scorecard_config_sid}`,
        agentName,
      }
    })
  }, [items])

  // The streak alert is inherently a (criterion, agent) pair, so the
  // drilldown should narrow BOTH at once. CriterionDetailDrawer honors
  // an optional `agentContext` on the payload -- when present, all of
  // its fetches add agent_ids and the header shows a "Filtered to X"
  // pill. The Top-Agents-Failing-This panel hides itself (trivial when
  // it's just one agent).
  const handleClick = (row) => {
    if (!clickEntity) {
      return
    }
    const fullName =
      `${row.first_name || ''} ${row.last_name || ''}`.trim() || `User ${row.user_id}`
    clickEntity({
      type: 'criterion',
      payload: {
        criteria_id: row.criteria_id,
        id: row.criteria_id,
        name: row.criteria_name,
        criteria_name: row.criteria_name,
        scorecard_config_sid: row.scorecard_config_sid,
        agentContext: {
          user_id: row.user_id,
          full_name: fullName,
        },
      },
    })
  }

  const columns = [
    { accessor: 'agentName', label: 'Agent' },
    {
      accessor: 'criteria_name',
      label: 'Criterion',
      format: (value, row) => (
        <button type="button" className="table-link" onClick={() => handleClick(row)}>
          {value || row.criteria_id}
        </button>
      ),
    },
    {
      accessor: 'streak_length',
      label: 'Streak',
      format: (value) => <span className="failure-streak-alerts-streak">{value}x</span>,
    },
    {
      accessor: 'last_failed_at',
      label: 'Last fail',
      format: (value) => <span className="failure-streak-alerts-meta">{formatDate(value)}</span>,
    },
  ]

  const actions = [
    {
      label: 'show-calls',
      trigger: (row) => (
        <ShowCallsButton
          as="icon"
          title={`Show ${row.agentName}'s failing calls on this scorecard`}
          scope={{
            agents: [{ value: row.user_id }],
            scorecards: row.scorecard_config_sid
              ? [{ value: row.scorecard_config_sid }]
              : undefined,
          }}
          tier="below"
        />
      ),
    },
  ]

  const hasNoData = !loading && !err && isEmpty(rows)

  return (
    <Card
      title={`Failure Streak Alerts (>=${MIN_STREAK} in a row)`}
      info="Agents currently on a run of consecutive failures on the same criterion — an early signal to coach before the streak drags their score down."
    >
      <WidgetActionHint kind="drawer">
        Click a criterion to investigate it for that agent
      </WidgetActionHint>
      {err && <div className="entity-drawer-empty">{err}</div>}
      {!err && hasNoData && (
        <div className="entity-drawer-empty">
          No agents have failed any criterion {MIN_STREAK}+ times in a row.
        </div>
      )}
      {!err && !hasNoData && loading && <div className="entity-drawer-empty">Loading streaks…</div>}
      {!err && !hasNoData && !loading && (
        <div className="failure-streak-alerts-table-wrap">
          <AdvancedTable
            testid="failure-streak-alerts-table"
            index="id"
            columns={columns}
            rows={rows}
            actions={actions}
            defaultOrderBy="streak_length"
            defaultOrder="desc"
            wrapColumnContent={false}
          />
        </div>
      )}
    </Card>
  )
}
